import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLoading } from '../context/LoadingContext';
import api from '../utils/api';
import PageTransition from '../components/PageTransition';
import { 
  FaEnvelope, 
  FaPhoneAlt, 
  FaClock, 
  FaArrowRight,
  FaCheck,
  FaExclamationCircle
} from 'react-icons/fa';

const Contact = () => {
  const { setLoading } = useLoading();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }
    
    if (formData.phone && !/^[\d\s\-+()]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setLoading(true);
      setSubmitStatus(null);
      
      const response = await api.post('/contacts', formData);
      
      setSubmitStatus({
        type: 'success',
        message: response.data.message || 'Thank you for contacting us! We will get back to you soon.'
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });
      
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.message || 'Failed to submit the form. Please try again later.'
      });
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
    },
  };

  return (
    <PageTransition>
    <div className="min-h-screen bg-transparent pt-24">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(255,255,255,0.02) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(255,255,255,0.02) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        {/* Gradient Orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-gray-800 to-gray-950 rounded-full blur-3xl opacity-20" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-gray-900 to-black rounded-full blur-3xl opacity-20" />
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center gap-2 px-4 py-2 mb-6"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-900 rounded-full blur-md opacity-50" />
              <div className="relative flex items-center gap-2 px-4 py-2 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-full">
                <span className="text-xs font-medium tracking-wider text-gray-300 uppercase">
                  Get In Touch
                </span>
              </div>
            </div>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="text-5xl md:text-6xl lg:text-7xl font-black mb-6"
          >
            <span className="text-white">Let's</span>
            <br />
            <span className="bg-gradient-to-r from-gray-300 via-gray-100 to-gray-400 bg-clip-text text-transparent">
              Work Together
            </span>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed"
          >
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </motion.p>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Information */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-8"
            >
              <motion.div variants={itemVariants}>
                <h2 className="text-3xl font-bold text-white mb-6">Contact Information</h2>
                <div className="h-1 w-20 bg-gradient-to-r from-gray-400 to-gray-600 rounded-full" />
              </motion.div>

              {/* Address */}
              {/* <motion.div
                variants={itemVariants}
                className="group relative p-6 bg-gray-900/30 backdrop-blur-sm border border-gray-800 rounded-2xl hover:border-gray-700 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
                <div className="relative flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 flex items-center justify-center flex-shrink-0">
                    <FaMapMarkerAlt className="text-gray-400 text-xl group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Visit Us</h3>
                    <p className="text-gray-400 leading-relaxed">
                      123 Business Street<br />
                      New York, NY 10001<br />
                      United States
                    </p>
                  </div>
                </div>
              </motion.div> */}

              {/* Email */}
              <motion.div
                variants={itemVariants}
                className="group relative p-6 bg-gray-900/30 backdrop-blur-sm border border-gray-800 rounded-2xl hover:border-gray-700 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
                <div className="relative flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 flex items-center justify-center flex-shrink-0">
                    <FaEnvelope className="text-gray-400 text-xl group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Email Us</h3>
                    <div className="space-y-1">
                      <a
                        href="mailto:contact@zynexistechnologies.com"
                        className="text-gray-400 hover:text-white transition-colors duration-300 block"
                      >
                        contact@zynexistechnologies.com
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Phone */}
              <motion.div
                variants={itemVariants}
                className="group relative p-6 bg-gray-900/30 backdrop-blur-sm border border-gray-800 rounded-2xl hover:border-gray-700 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
                <div className="relative flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 flex items-center justify-center flex-shrink-0">
                    <FaPhoneAlt className="text-gray-400 text-xl group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Call Us</h3>
                    <div className="space-y-1">
                      <a
                        href="tel:+918799765489"
                        className="text-gray-400 hover:text-white transition-colors duration-300 block"
                      >
                        +91 87997 65489
                      </a>
                      <a
                        href="tel:+919811721537"
                        className="text-gray-400 hover:text-white transition-colors duration-300 block"
                      >
                        +91 98117 21537
                      </a>
                      <a
                        href="tel:+919348863495"
                        className="text-gray-400 hover:text-white transition-colors duration-300 block"
                      >
                        +91 93488 63495
                      </a>
                    </div>
                    <span className="text-xs text-gray-600 mt-2 block">Mon-Fri: 9am–6pm IST &nbsp;|&nbsp; Sat-Sun: 10am–4pm IST</span>
                  </div>
                </div>
              </motion.div>

              {/* Business Hours */}
              <motion.div
                variants={itemVariants}
                className="group relative p-6 bg-gray-900/30 backdrop-blur-sm border border-gray-800 rounded-2xl hover:border-gray-700 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
                <div className="relative flex gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 flex items-center justify-center flex-shrink-0">
                    <FaClock className="text-gray-400 text-xl group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Business Hours</h3>
                    <div className="space-y-1 text-gray-400">
                      <p><span className="text-gray-300">Mon-Fri:</span> 9:00 AM - 6:00 PM IST</p>
                      <p><span className="text-gray-300">Sat-Sun:</span> 10:00 AM - 4:00 PM IST</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div
                variants={itemVariants}
                className="relative p-8 bg-gray-900/30 backdrop-blur-sm border border-gray-800 rounded-2xl"
              >
                {/* Form Header */}
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white mb-2">Send us a Message</h2>
                  <p className="text-gray-400 text-sm">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                </div>

                {/* Status Message */}
                {submitStatus && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
                      submitStatus.type === 'success' 
                        ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                        : 'bg-red-500/10 border border-red-500/20 text-red-400'
                    }`}
                  >
                    {submitStatus.type === 'success' ? (
                      <FaCheck className="text-green-400 flex-shrink-0" />
                    ) : (
                      <FaExclamationCircle className="text-red-400 flex-shrink-0" />
                    )}
                    <p className="text-sm">{submitStatus.message}</p>
                  </motion.div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-900/50 border ${
                        errors.name ? 'border-red-500/50' : 'border-gray-800'
                      } rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-gray-700 transition-colors duration-300`}
                      placeholder="Rahul Sharma"
                    />
                    {errors.name && (
                      <p className="mt-2 text-sm text-red-400">{errors.name}</p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-900/50 border ${
                        errors.email ? 'border-red-500/50' : 'border-gray-800'
                      } rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-gray-700 transition-colors duration-300`}
                      placeholder="rahul.sharma@example.in"
                    />
                    {errors.email && (
                      <p className="mt-2 text-sm text-red-400">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone & Company Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-gray-900/50 border ${
                          errors.phone ? 'border-red-500/50' : 'border-gray-800'
                        } rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-gray-700 transition-colors duration-300`}
                        placeholder="+91 98765 43210"
                      />
                      {errors.phone && (
                        <p className="mt-2 text-sm text-red-400">{errors.phone}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-gray-700 transition-colors duration-300"
                        placeholder="Tata Consultancy Services"
                      />
                    </div>
                  </div>

                  {/* Subject Field */}
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-900/50 border ${
                        errors.subject ? 'border-red-500/50' : 'border-gray-800'
                      } rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-gray-700 transition-colors duration-300`}
                      placeholder="What is this about?"
                    />
                    {errors.subject && (
                      <p className="mt-2 text-sm text-red-400">{errors.subject}</p>
                    )}
                  </div>

                  {/* Message Field */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="6"
                      className={`w-full px-4 py-3 bg-gray-900/50 border ${
                        errors.message ? 'border-red-500/50' : 'border-gray-800'
                      } rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-gray-700 transition-colors duration-300 resize-none`}
                      placeholder="Tell us more about your inquiry..."
                    />
                    {errors.message && (
                      <p className="mt-2 text-sm text-red-400">{errors.message}</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <motion.button
                    type="submit"
                    className="w-full group relative px-8 py-4 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold rounded-xl overflow-hidden transition-all duration-300 inline-flex items-center justify-center gap-2 border border-gray-700 hover:border-gray-600"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-gray-700 to-gray-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <span className="relative flex items-center gap-2">
                      Send Message
                      <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </motion.button>

                  <p className="text-xs text-gray-600 text-center">
                    * Required fields. We respect your privacy and will never spam you.
                  </p>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-950 to-transparent pointer-events-none" />
    </div>
    </PageTransition>
  );
};

export default Contact;